import { features } from '!/flags'
import {
  AdditionalLinkContainer,
  ErrorMessage,
  ForgotPasswordLink,
  InlineButtonAdditional2,
  LoginPasswordInputBlock,
  LoginUsernameInputBlock,
  PrimarySubmitButton,
  RememberCheckBox,
  SignInActionContainer,
  SignInFormTemplate,
  SignUpInputContainerSC,
  SocialAppleButtonTemplate,
  SocialFacebookButtonTemplate,
  SocialGoogleButtonTemplate,
  Text,
  TextTag,
  TitleLineDivider,
  Wrapper,
} from '@setplex/arcane'
import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { type ChangeEvent, type FormEvent } from 'react'
import { SignUpMethod } from '~/shared/analytics/index.h'
import { PATH } from '~/shared/constants/path'
import { addGoogleAnalyticsPrefix } from '~/shared/helpers'
import * as signUpModel from '~/widgets/auth/sign-up/model'
import { useGetRedirectUrl } from '../../helper'
import { socialAuthStarted } from '../../model'
import {
  $canSubmit,
  $error,
  resetErrorOnClick,
  signInForm,
  signInFx,
} from '../model'

SignInForm.displayName = 'SignInForm'
export function SignInForm() {
  const pending = useUnit(signInFx.pending)
  const error = useUnit($error)
  const { submit, fields, eachValid, hasError, errorText } = useForm(signInForm)
  const canSubmit = useUnit($canSubmit)

  const facebookEnabled = useUnit(features.$facebookEnabled)
  const googleEnabled = useUnit(features.$googleEnabled)
  const appleEnabled = useUnit(features.$appleEnabled)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const onOpenSignUpPopup = () => signUpModel.openSignUpPopup()

  const redirectUrl = useGetRedirectUrl()
  const onSocialAuth = useUnit(socialAuthStarted)

  return (
    <SignInFormTemplate
      noValidate
      onSubmit={onSubmit}
      onClick={resetErrorOnClick}
    >
      <Text
        text='Sign In'
        className='text--fonts-subtitle-xl-bold text--color-on-surface-1 signin-title text--center'
        tag={TextTag.H1}
      />

      <Text
        text='It’s FREE. No subscription required.'
        className='text--fonts-body-xs-medium text--color-on-surface-2 signin-subtitle text--center'
      />

      <Wrapper className='auth-button-group-template wrapper--justify-content-center'>
        {facebookEnabled && (
          <SocialFacebookButtonTemplate
            href={`/api/web/auth/facebook${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Facebook)}
            classNameGA={addGoogleAnalyticsPrefix('sign-in-facebook')}
          />
        )}
        {appleEnabled && (
          <SocialAppleButtonTemplate
            href={`/api/web/auth/apple${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Apple)}
            classNameGA={addGoogleAnalyticsPrefix('sign-in-apple')}
          />
        )}
        {googleEnabled && (
          <SocialGoogleButtonTemplate
            href={`/api/web/auth/google${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Google)}
            classNameGA={addGoogleAnalyticsPrefix('sign-in-google')}
          />
        )}
      </Wrapper>

      <TitleLineDivider title='or' />

      <SignUpInputContainerSC>
        <LoginUsernameInputBlock
          name='email'
          value={fields.email.value}
          placeholder='Email'
          label='Email'
          autoComplete='username'
          disabled={pending}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            fields.email.onChange(e.target.value)
          }
          errorMessage={
            hasError('email')
              ? errorText('email', {
                  required: 'Please, enter email',
                  email: 'Please, enter a valid email',
                })
              : ''
          }
        />

        <LoginPasswordInputBlock
          name='password'
          value={fields.password.value}
          placeholder='Password'
          label='Password'
          autoComplete='current-password'
          disabled={pending}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            fields.password.onChange(e.target.value)
          }
          errorMessage={
            hasError('password')
              ? errorText('password', {
                  required: 'Please, enter password',
                  password:
                    'Please, enter a valid password. Only letters (aA-zZ), numbers (0-9) and symbols (@#$_&-*!?.) are allowed',
                  maxLength:
                    'Password is too long, maximum length is 64 characters',
                })
              : ''
          }
        />
      </SignUpInputContainerSC>

      <AdditionalLinkContainer>
        <RememberCheckBox
          id='rememberMe'
          name='rememberMe'
          onChange={(e) => fields.rememberMe.onChange(e.target.checked)}
        />
        <ForgotPasswordLink>
          <InlineButtonAdditional2
            title='Forgot password?'
            href={PATH.FORGOT_PASSWORD}
          />
        </ForgotPasswordLink>
      </AdditionalLinkContainer>

      <PrimarySubmitButton
        classNameGA={addGoogleAnalyticsPrefix('sign-in-form')}
        title='Sign In'
        disabled={pending || !eachValid || Boolean(error) || !canSubmit}
        isLoading={pending}
      />

      <SignInActionContainer>
        <Wrapper className='text-block-body-xs-medium-with-inline-button wrapper--justify-content-center'>
          <Text
            className='text--fonts-body-xs-medium text--color-on-surface-2'
            text="Don't have an account?"
          />
          <InlineButtonAdditional2
            title='Register Free'
            onClick={onOpenSignUpPopup}
          />
        </Wrapper>
      </SignInActionContainer>

      {error && (
        <ErrorMessage
          title={error}
          maxLines={0}
          resetError={resetErrorOnClick}
        />
      )}
    </SignInFormTemplate>
  )
}
